import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FilterListRounded,
  MoreVertRounded,
  SearchRounded,
  CloseRounded,
  AddRounded,
  KeyboardArrowDownRounded,
  DriveFileRenameOutlineRounded,
  DeleteRounded,
  AccountBalanceRounded,
  SwapHorizRounded,
  TrendingUpRounded,
  TrendingDownRounded,
  CalendarTodayRounded,
  DescriptionRounded,
  ReceiptRounded,
  AccountBalanceWallet,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { backend_api } from "../../../static/server";
import styles, {
  formatteDateDay,
  generateUniqueMatterName,
  pageScrollStyle,
} from "../../../styles/styles";
import { useDialog } from "../../DialogContext";
import DataTable from "../../DataTable";
import NewTransactionForm from "./AccountsForms/NewTransactionForm";
import TransferFundForm from "./AccountsForms/TranferFundFrom";
import {
  getAllStatement,
  getBankDetail,
} from "../../../redux/Apps/Lawyerapp/AccountSlice/AccountSlice";
import FilterDrawer from "../../../components/FilterDrawer";
import { DataGridNoData } from "../../../static/staticdata";
import moneyTransferImg from "../../../assets/money-tranfer.webp";
import DateRangeFilter from "../../../components/DateRangeFilter";
import { DateCalendar } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { CustomToolbarWithSearch } from "../../CustomToolbarWithSearch";
import { getCurrencySymbol } from "../../../pages/LawyerPages/Settings/CurrencySetForm";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const BankTransaction = () => {
  const { showDialog } = useDialog();
  const dispatch = useDispatch();
  const { id: accountId, accountName } = useParams();
  const decodedBankName = decodeURIComponent(accountName).replace(/-/g, " ");

  // Redux selectors
  const { token } = useSelector((data) => data.user);
  const { bankDetail, allStatement, loading } = useSelector(
    (state) => state.account,
  );
  const { pageSize, rowCount, nextPage, previousPage, count } = useSelector(
    (state) => state.account.statementPagination,
  );

  // State management
  const [page, setPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(pageSize || 10);
  const [searchTransaction, setSearchTransaction] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTransaction, setDeleteTransaction] = useState(null);
  const [deleteLoad, setDeleteLoad] = useState(false);
  const [actionMenu, setActionMenu] = useState({
    anchorEl: null,
    rowData: null,
  });
  const [activeTab, setActiveTab] = useState("all");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [bankDetailMenu, setBankDetailMenu] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    transaction_type: [],
    status: [],
    client: null,
    matter: null,
    amount_range: { min: null, max: null },
  });
  const [dateFilters, setDateFilters] = useState({
    date__gte: null,
    date__lte: null,
  });
  const [selectedDateType, setSelectedDateType] = useState("");
  const [selectedSpecificDate, setSelectedSpecificDate] = useState(null);

  const filterOptions = {
    transaction_type: [
      {
        value: "deposit",
        label: "Deposit",
        color: "#4CAF50",
        icon: <TrendingUpRounded />,
      },
      {
        value: "withdrawal",
        label: "Withdrawal",
        color: "#F44336",
        icon: <TrendingDownRounded />,
      },
      {
        value: "transfer",
        label: "Transfer",
        color: "#2196F3",
        icon: <SwapHorizRounded />,
      },
    ],
    status: [
      { value: "pending", label: "Pending", color: "#FF9800" },
      { value: "completed", label: "Completed", color: "#4CAF50" },
      { value: "failed", label: "Failed", color: "#F44336" },
      { value: "reconciled", label: "Reconciled", color: "#9C27B0" },
    ],
    dateTypeOptions: [
      { value: "date", label: "Transaction Date" },
      { value: "created_at", label: "Created Date" },
    ],
  };

  const isFilterApplied = useMemo(() => {
    return (
      activeTab !== "all" ||
      activeFilters.transaction_type.length > 0 ||
      activeFilters.status.length > 0 ||
      activeFilters.client !== null ||
      activeFilters.matter !== null ||
      activeFilters.amount_range.min !== null ||
      activeFilters.amount_range.max !== null ||
      dateFilters.date__gte !== null ||
      dateFilters.date__lte !== null ||
      selectedDateType !== "" ||
      selectedSpecificDate !== null
    );
  }, [
    activeTab,
    activeFilters,
    dateFilters,
    selectedDateType,
    selectedSpecificDate,
  ]);

  const fetchTransactions = useCallback(() => {
    let queryParams = [`account=${accountId}`];

    // Tab filter
    if (activeTab !== "all") {
      if (activeTab === "deposits") {
        queryParams.push(`transaction_type=deposit`);
      } else if (activeTab === "withdrawals") {
        queryParams.push(`transaction_type=withdrawal`);
      } else if (activeTab === "transfers") {
        queryParams.push(`transaction_type=transfer`);
      }
    }

    // Transaction type filters
    if (activeFilters.transaction_type.length > 0) {
      queryParams.push(
        `transaction_type=${activeFilters.transaction_type.join(",")}`,
      );
    }

    // Status filters
    if (activeFilters.status.length > 0) {
      queryParams.push(`status=${activeFilters.status.join(",")}`);
    }

    // Client filter
    if (activeFilters.client) {
      queryParams.push(`client=${activeFilters.client}`);
    }

    // Matter filter
    if (activeFilters.matter) {
      queryParams.push(`matter=${activeFilters.matter}`);
    }

    // Amount range filters
    if (activeFilters.amount_range.min !== null) {
      queryParams.push(`amount__gte=${activeFilters.amount_range.min}`);
    }
    if (activeFilters.amount_range.max !== null) {
      queryParams.push(`amount__lte=${activeFilters.amount_range.max}`);
    }

    // Date range filters
    if (dateFilters.date__gte) {
      queryParams.push(`date__gte=${dateFilters.date__gte}`);
    }
    if (dateFilters.date__lte) {
      queryParams.push(`date__lte=${dateFilters.date__lte}`);
    }

    // Specific date filter
    if (selectedDateType && selectedSpecificDate) {
      const formattedDate = dayjs(selectedSpecificDate).format("YYYY-MM-DD");
      queryParams.push(`${selectedDateType}=${formattedDate}`);
    }

    // Search filter
    if (searchTransaction) {
      queryParams.push(`search=${encodeURIComponent(searchTransaction)}`);
    }

    const queryString = queryParams.join("&");
    const url = `${backend_api}bank/transactions/?${queryString}`;

    dispatch(getAllStatement({ url, token, page, pageSize: localPageSize }));
  }, [
    dispatch,
    token,
    accountId,
    page,
    localPageSize,
    activeTab,
    activeFilters,
    dateFilters,
    selectedDateType,
    selectedSpecificDate,
    searchTransaction,
  ]);

  const fetchBankDetail = useCallback(() => {
    dispatch(
      getBankDetail({
        url: `${backend_api}bank/accounts/${accountId}/`,
        token,
      }),
    );
  }, [dispatch, token, accountId]);

  useEffect(() => {
    fetchTransactions();
    fetchBankDetail();
  }, [fetchTransactions, fetchBankDetail]);

  // Filter handlers
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handleTransactionTypeFilter = (type) => {
    setActiveFilters((prev) => {
      const newTypes = prev.transaction_type.includes(type)
        ? prev.transaction_type.filter((t) => t !== type)
        : [...prev.transaction_type, type];
      return { ...prev, transaction_type: newTypes };
    });
    setPage(1);
  };

  const handleStatusFilter = (status) => {
    setActiveFilters((prev) => {
      const newStatuses = prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status];
      return { ...prev, status: newStatuses };
    });
    setPage(1);
  };

  const handleAmountRangeChange = (min, max) => {
    setActiveFilters((prev) => ({
      ...prev,
      amount_range: { min, max },
    }));
    setPage(1);
  };

  const handleDateRangeChange = (range, dateType) => {
    if (dateType === "date") {
      setDateFilters((prev) => ({
        ...prev,
        date__gte: range.date__gte || null,
        date__lte: range.date__lte || null,
      }));
    }
    setPage(1);
  };

  const handleDateTypeChange = (event) => {
    const newType = event.target.value;
    setSelectedDateType(newType);
    setSelectedSpecificDate(null);
    setPage(1);
  };

  const handleSpecificDateChange = (date) => {
    setSelectedSpecificDate(date);
    setPage(1);
  };

  const handleApplyFilters = () => {
    setPage(1);
    setFilterAnchor(null);
  };

  const handleResetFilters = () => {
    setActiveTab("all");
    setActiveFilters({
      transaction_type: [],
      status: [],
      client: null,
      matter: null,
      amount_range: { min: null, max: null },
    });
    setDateFilters({
      date__gte: null,
      date__lte: null,
    });
    setSelectedDateType("");
    setSelectedSpecificDate(null);
    setSearchTransaction("");
    setPage(1);
    setFilterAnchor(null);
  };

  // Transaction operations
  const handleOpenDeleteDialog = (transaction) => {
    setDeleteTransaction(transaction);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTransaction(null);
  };

  const handleDeleteTransaction = async () => {
    if (!deleteTransaction) return;

    setDeleteLoad(true);
    try {
      const response = await fetch(
        `${backend_api}bank/transactions/${deleteTransaction.id}/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );

      if (response.status === 204) {
        toast.success("Transaction deleted successfully");
        fetchTransactions();
      }
    } catch (error) {
      toast.error("Failed to delete transaction");
    } finally {
      setDeleteLoad(false);
      handleCloseDeleteDialog();
    }
  };

  const handleEditTransaction = (transaction) => {
    showDialog(
      <NewTransactionForm
        accountId={accountId}
        TransactionId={transaction.id}
        accountType={bankDetail?.account_type}
        ClientAccountId={bankDetail?.client_account}
        isEditMode={true}
      />,
      {
        dialogTitle: "Edit Transaction",
      },
    );
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = useCallback((newPageSize) => {
    setLocalPageSize(newPageSize);
    setPage(1);
  }, []);

  const handleActionMenuOpen = (event, row) => {
    setActionMenu({
      anchorEl: event.currentTarget,
      rowData: row,
    });
  };

  const handleActionMenuClose = () => {
    setActionMenu({ anchorEl: null, rowData: null });
  };

  const handleBankDetailMenuOpen = (event) => {
    setBankDetailMenu(event.currentTarget);
  };

  const handleBankDetailMenuClose = () => {
    setBankDetailMenu(null);
  };

  // Active filter chips
  const activeFilterChips = useMemo(() => {
    const chips = [];

    // Tab chip
    if (activeTab !== "all") {
      const tabLabel =
        activeTab === "deposits"
          ? "Deposits"
          : activeTab === "withdrawals"
            ? "Withdrawals"
            : activeTab === "transfers"
              ? "Transfers"
              : activeTab;

      chips.push(
        <Chip
          key="tab-filter"
          label={`Type: ${tabLabel}`}
          size="small"
          onDelete={() => handleTabChange("all")}
          sx={{
            backgroundColor: "#2196F315",
            color: "#2196F3",
            border: "1px solid #2196F330",
            maxWidth: 160,
          }}
        />,
      );
    }

    // Transaction type chips
    activeFilters.transaction_type.forEach((type) => {
      const option = filterOptions.transaction_type.find(
        (opt) => opt.value === type,
      );
      if (option) {
        chips.push(
          <Chip
            key={`type-${type}`}
            label={`Type: ${option.label}`}
            size="small"
            onDelete={() => handleTransactionTypeFilter(type)}
            sx={{
              backgroundColor: `${option.color}15`,
              color: option.color,
              border: `1px solid ${option.color}30`,
              maxWidth: 160,
            }}
          />,
        );
      }
    });

    // Status chips
    activeFilters.status.forEach((status) => {
      const option = filterOptions.status.find((opt) => opt.value === status);
      if (option) {
        chips.push(
          <Chip
            key={`status-${status}`}
            label={`Status: ${option.label}`}
            size="small"
            onDelete={() => handleStatusFilter(status)}
            sx={{
              backgroundColor: `${option.color}15`,
              color: option.color,
              border: `1px solid ${option.color}30`,
              maxWidth: 160,
            }}
          />,
        );
      }
    });

    // Amount range chip
    if (
      activeFilters.amount_range.min !== null ||
      activeFilters.amount_range.max !== null
    ) {
      const min = activeFilters.amount_range.min || "0";
      const max = activeFilters.amount_range.max || "∞";
      chips.push(
        <Chip
          key="amount-range"
          label={`Amount: $${min} - $${max}`}
          size="small"
          onDelete={() => handleAmountRangeChange(null, null)}
          sx={{
            backgroundColor: "#FF980015",
            color: "#FF9800",
            border: "1px solid #FF980030",
            maxWidth: 200,
          }}
        />,
      );
    }

    // Date range chips
    if (dateFilters.date__gte && dateFilters.date__lte) {
      chips.push(
        <Chip
          key="date-range"
          label={`Date: ${dateFilters.date__gte} to ${dateFilters.date__lte}`}
          size="small"
          onDelete={() => {
            setDateFilters({
              date__gte: null,
              date__lte: null,
            });
            setPage(1);
          }}
          sx={{
            backgroundColor: "#9C27B015",
            color: "#9C27B0",
            border: "1px solid #9C27B030",
            maxWidth: 200,
          }}
        />,
      );
    }

    // Specific date chip
    if (selectedDateType && selectedSpecificDate) {
      const dateTypeLabel = filterOptions.dateTypeOptions.find(
        (opt) => opt.value === selectedDateType,
      )?.label;
      const displayDate = dayjs(selectedSpecificDate).format("YYYY-MM-DD");

      chips.push(
        <Chip
          key={`specific-date-${selectedDateType}`}
          label={`${dateTypeLabel}: ${displayDate}`}
          size="small"
          onDelete={() => {
            setSelectedDateType("");
            setSelectedSpecificDate(null);
            setPage(1);
          }}
          sx={{
            backgroundColor: "#2196F315",
            color: "#2196F3",
            border: "1px solid #2196F330",
            maxWidth: 200,
          }}
        />,
      );
    }

    if (chips.length > 0) {
      chips.unshift(
        <Typography
          key="active-filters-label"
          variant="body2"
          sx={{
            fontWeight: 600,
            flexShrink: 0,
            color: "text.secondary",
            fontSize: "0.875rem",
          }}
        >
          Active Filters:
        </Typography>,
      );
    }

    return chips;
  }, [
    activeTab,
    activeFilters,
    dateFilters,
    selectedDateType,
    selectedSpecificDate,
  ]);

  // Columns configuration
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
          }}
        >
          <Tooltip title="More actions">
            <IconButton
              size="small"
              onClick={(e) => handleActionMenuOpen(e, params.row)}
            >
              <MoreVertRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={actionMenu.anchorEl}
            open={Boolean(actionMenu.anchorEl)}
            onClose={handleActionMenuClose}
          >
            <MenuItem
              onClick={() => {
                handleEditTransaction(actionMenu?.rowData);
                handleActionMenuClose();
              }}
            >
              <DriveFileRenameOutlineRounded fontSize="small" sx={{ mr: 1 }} />
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleOpenDeleteDialog(actionMenu.rowData);
                handleActionMenuClose();
              }}
              sx={{ color: "error.main" }}
            >
              <DeleteRounded fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </Menu>
        </Box>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <CalendarTodayRounded
            sx={{ fontSize: "0.9rem", color: "text.secondary" }}
          />
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.date}
          </Typography>
        </Box>
      ),
    },
    {
      field: "transaction_type",
      headerName: "Type",
      width: 120,
      renderCell: (params) => {
        const option = filterOptions.transaction_type.find(
          (opt) => opt.value === params.row.transaction_type,
        );

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {option?.icon &&
              React.cloneElement(option.icon, {
                sx: { fontSize: "0.9rem", color: option.color },
              })}
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                color: option?.color || "text.primary",
                fontWeight: 500,
              }}
            >
              {option?.label || params.row.transaction_type}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Tooltip
          title={params?.row?.description || "No description"}
          arrow
          placement="top-start"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <DescriptionRounded
              sx={{ fontSize: "0.9rem", color: "text.secondary" }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
              title={params.row.description}
            >
              {params.row.description}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "client",
      headerName: "Client",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {params.row.client}
          </Typography>
        </Box>
      ),
    },
    {
      field: "matter",
      headerName: "Matter",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
          {params.row.matter}
        </Typography>
      ),
    },
    {
      field: "reference",
      headerName: "Reference",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ReceiptRounded
            sx={{ fontSize: "0.9rem", color: "text.secondary" }}
          />
          <Typography
            variant="body2"
            sx={{ fontSize: "0.85rem", fontFamily: "monospace" }}
          >
            {params.row.reference}
          </Typography>
        </Box>
      ),
    },
    {
      field: "currency",
      headerName: "Currency",
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.row.currency}
          size="small"
          variant="outlined"
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            backgroundColor: "#f0f0f0",
          }}
        />
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      renderCell: (params) => {
        const isDeposit = params.row.transaction_type === "deposit";
        const isWithdrawal = params.row.transaction_type === "withdrawal";

        return (
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: isDeposit
                ? "success.main"
                : isWithdrawal
                  ? "error.main"
                  : "text.primary",
            }}
          >
            {isDeposit
              ? `+ ${getCurrencySymbol(params?.row?.currency)}${params.row.fund_in}`
              : isWithdrawal
                ? `- ${getCurrencySymbol(params?.row?.currency)}${params.row.fund_out}`
                : `${getCurrencySymbol(params?.row?.currency)}  ${params.row.amount}`}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const option = filterOptions.status.find(
          (opt) => opt.value === params.row.status,
        );

        return (
          <Chip
            label={option?.label || params.row.status}
            size="small"
            sx={{
              backgroundColor: `${option?.color || "#9E9E9E"}15`,
              color: option?.color || "#9E9E9E",
              border: `1px solid ${option?.color || "#9E9E9E"}30`,
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          />
        );
      },
    },
    {
      field: "running_balance",
      headerName: "Balance",
      width: 120,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            color:
              parseFloat(params.row.running_balance) >= 0
                ? "success.main"
                : "error.main",
          }}
        >
          {getCurrencySymbol(params?.row?.currency)}
          {params.row.running_balance}
        </Typography>
      ),
    },
  ];

  // Rows data
  const rows = useMemo(() => {
    if (!Array.isArray(allStatement)) return [];

    return allStatement?.map((item) => ({
      id: item.id,
      date: item.date ? formatteDateDay(item?.date) : "-",
      transaction_type: item.transaction_type || "-",
      description: item.description || "-",
      reference: item.check_reference_no || "-",
      client: item.client
        ? `${item.client.first_name || ""} ${
            item.client.last_name || ""
          }`.trim()
        : "-",
      matter: item.matter
        ? generateUniqueMatterName(item.matter.id, item.matter.case_name)
        : "-",
      fund_out: item.transaction_type === "withdrawal" ? item.amount : "-",
      fund_in: item.transaction_type === "deposit" ? item.amount : "-",
      currency: item.currency || "USD",
      amount: item?.amount || "0.00",
      status: item.status || "pending",
      running_balance: item.balance_amount || "0.00",
      full_data: item,
    }));
  }, [allStatement]);

  const extraBtn = useCallback(() => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: "0.5rem",
          p: "0.2rem",
          borderRadius: "0.5rem",
        }}
      >
        <Button
          size="small"
          variant={activeTab === "all" ? "contained" : "text"}
          sx={{
            textTransform: "capitalize",
            fontSize: "0.8rem",
            transition: "0.3s ease-in-out",
          }}
          onClick={() => handleTabChange("all")}
        >
          All Transactions
        </Button>
        <Button
          size="small"
          variant={activeTab === "deposits" ? "contained" : "text"}
          sx={{
            textTransform: "capitalize",
            fontSize: "0.8rem",
            transition: "0.3s ease-in-out",
          }}
          onClick={() => handleTabChange("deposits")}
        >
          Deposits
        </Button>
        <Button
          size="small"
          variant={activeTab === "withdrawals" ? "contained" : "text"}
          sx={{
            textTransform: "capitalize",
            fontSize: "0.8rem",
            transition: "0.3s ease-in-out",
          }}
          onClick={() => handleTabChange("withdrawals")}
        >
          Withdrawals
        </Button>
        {/* <Button
          size="small"
          variant={activeTab === "transfers" ? "contained" : "text"}
          sx={{
            textTransform: "capitalize",
            fontSize: "0.8rem",
            transition: "0.3s ease-in-out",
          }}
          onClick={() => handleTabChange("transfers")}
        >
          Transfers
        </Button> */}
      </Box>
    );
  }, [activeTab]);

  // Bank account summary stats
  const accountStats = useMemo(() => {
    // 🛡️ Safely handle allStatement if it's not an array
    const statements = Array.isArray(allStatement) ? allStatement : [];

    if (statements.length === 0) {
      return { totalDeposits: 0, totalWithdrawals: 0, netFlow: 0 };
    }

    const totalDeposits = statements
      .filter((t) => t.transaction_type === "deposit")
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

    const totalWithdrawals = statements
      .filter((t) => t.transaction_type === "withdrawal")
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

    const netFlow = totalDeposits - totalWithdrawals;

    return { totalDeposits, totalWithdrawals, netFlow };
  }, [allStatement]);

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "1rem",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccountBalanceRounded
            sx={{ fontSize: "1.5rem", color: "primary.main" }}
          />
          <Box>
            <h3
              className={`${styles.title}`}
              style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600 }}
            >
              {decodedBankName}
            </h3>
            <Typography variant="h5" sx={{ fontWeight: 600 }}></Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Account #{bankDetail?.account_number || "—"}
              </Typography>
              {bankDetail?.is_default && (
                <Chip
                  label="Default Account"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    backgroundColor: "primary.main",
                    color: "white",
                  }}
                />
              )}
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleBankDetailMenuOpen}
            sx={{ ml: 0.5 }}
          >
            <KeyboardArrowDownRounded sx={{ fontSize: "1rem" }} />
          </IconButton>

          <Menu
            anchorEl={bankDetailMenu}
            open={Boolean(bankDetailMenu)}
            onClose={handleBankDetailMenuClose}
            PaperProps={{
              sx: {
                width: 300,
                maxHeight: 400,
                ...pageScrollStyle,
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Account Details
              </Typography>

              {bankDetail && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Account Holder
                    </Typography>
                    <Typography variant="body2">
                      {bankDetail.account_holder_name || "—"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Account Type
                    </Typography>
                    <Typography variant="body2">
                      {bankDetail.account_type || "—"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Balance
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {bankDetail.currency || "$"}{" "}
                      {parseFloat(bankDetail.balance || 0).toFixed(2)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Bank Details
                    </Typography>
                    <Typography variant="body2">
                      {bankDetail.bank_name || "—"}
                      {bankDetail.swift_code &&
                        ` (SWIFT: ${bankDetail.swift_code})`}
                    </Typography>
                  </Box>

                  {bankDetail.client && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Client
                      </Typography>
                      <Typography variant="body2">
                        {typeof bankDetail.client === "number"
                          ? bankDetail.client
                          : bankDetail.client?.first_name &&
                              bankDetail.client?.last_name
                            ? `${bankDetail.client.first_name} ${bankDetail.client.last_name}`
                            : bankDetail.client?.reminder_mail}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Menu>
        </Box>

        <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search transactions"
            value={searchTransaction}
            onChange={(e) => setSearchTransaction(e.target.value)}
            sx={{
              minWidth: "240px",
              fontSize: "0.8rem",
              "& .MuiOutlinedInput-root": {
                borderRadius: "32px",
                pr: 0.5,
                backgroundColor: "#f8f9fa",
              },
              "& .MuiInputBase-input::placeholder": {
                opacity: 0.5,
                fontSize: "0.8rem",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTransaction ? (
                    <IconButton
                      size="small"
                      onClick={() => setSearchTransaction("")}
                      sx={{
                        border: "0.1px solid #e5e5e5",
                        p: 0.5,
                        backgroundColor: "white",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      }}
                    >
                      <CloseRounded fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      disabled
                      sx={{
                        border: "0.1px solid #e5e5e5",
                        p: 0.5,
                        backgroundColor: "white",
                      }}
                    >
                      <SearchRounded fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />

          {/* Filter Button */}
          <Box>
            <Tooltip title="Filter" placement="bottom" arrow>
              <IconButton
                id="filter-button"
                size="small"
                onClick={(e) => setFilterAnchor(e.currentTarget)}
                sx={{
                  border: "1px solid",
                  borderColor: isFilterApplied ? "primary.main" : "divider",
                  bgcolor: isFilterApplied ? "primary.main" : "transparent",
                  color: isFilterApplied ? "#fff" : "text.secondary",
                  position: "relative",
                  width: 32,
                  height: 32,
                  transition: "0.3s ease-in-out",
                  "&:hover": {
                    bgcolor: isFilterApplied ? "primary.dark" : "action.hover",
                  },
                }}
              >
                <FilterListRounded fontSize="small" />
                {isFilterApplied && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "error.main",
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: "0.5rem" }}>
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: "capitalize" }}
              startIcon={<SwapHorizRounded />}
              onClick={() => {
                showDialog(
                  <TransferFundForm
                    accountId={accountId}
                    accountType={bankDetail?.account_type}
                  />,
                  {
                    dialogTitle: "Transfer Funds",
                  },
                );
              }}
            >
              Transfer Funds
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ textTransform: "capitalize" }}
              startIcon={<AddRounded />}
              onClick={() => {
                showDialog(
                  <NewTransactionForm
                    accountId={accountId}
                    accountType={bankDetail?.account_type}
                    {...(bankDetail?.account_type === "client fund"
                      ? { ClientAccountId: bankDetail?.client }
                      : {})}
                  />,
                  {
                    dialogTitle: "New Transaction",
                  },
                );
              }}
            >
              New Transaction
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Account Summary Stats */}
      <Box sx={{ display: "flex", gap: "1rem", mb: "1rem" }}>
        {/* Total Deposits */}
        <Box
          sx={{
            flex: 1,
            p: "1.5rem",
            borderRadius: "0.75rem",
            background: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
            border: "1px solid #A5D6A7",
            position: "relative",
            overflow: "hidden",
            transition: "transform 0.2s",
            "&:hover": {
              transform: "translateY(-0.25rem)",
            },
          }}
        >
          {/* Large Blended Background Icon */}
          <Box
            sx={{
              position: "absolute",
              right: "-1.25rem",
              bottom: "-1.25rem",
              opacity: 0.15,
              transform: "rotate(-15deg)",
            }}
          >
            <AccountBalanceWallet
              sx={{ fontSize: "8.75rem", color: "#2E7D32" }}
            />
          </Box>

          {/* Content */}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.03rem",
                fontSize: "0.7rem",
              }}
            >
              Total Deposits
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#1B5E20",
                mt: "0.5rem",
                fontSize: "1.5rem",
              }}
            >
              $
              {accountStats.totalDeposits.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Box>
        </Box>

        {/* Total Withdrawals */}
        <Box
          sx={{
            flex: 1,
            p: "1.5rem",
            borderRadius: "0.75rem",
            background: "linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)",
            border: "1px solid #EF9A9A",
            position: "relative",
            overflow: "hidden",
            transition: "transform 0.2s",
            "&:hover": {
              transform: "translateY(-0.25rem)",
            },
          }}
        >
          {/* Large Blended Background Icon */}
          <Box
            sx={{
              position: "absolute",
              right: "-1.25rem",
              bottom: "-1.25rem",
              opacity: 0.15,
              transform: "rotate(-15deg)",
            }}
          >
            <TrendingDown sx={{ fontSize: "8.75rem", color: "#C62828" }} />
          </Box>

          {/* Content */}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.03rem",
                fontSize: "0.7rem",
              }}
            >
              Total Withdrawals
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#C62828",
                mt: "0.5rem",
                fontSize: "1.5rem",
              }}
            >
              $
              {accountStats.totalWithdrawals.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Box>
        </Box>

        {/* Net Flow */}
        <Box
          sx={{
            flex: 1,
            p: "1.5rem",
            borderRadius: "0.75rem",
            background:
              accountStats.netFlow >= 0
                ? "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)"
                : "linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)",
            border: `1px solid ${
              accountStats.netFlow >= 0 ? "#A5D6A7" : "#EF9A9A"
            }`,
            position: "relative",
            overflow: "hidden",
            transition: "transform 0.2s",
            "&:hover": {
              transform: "translateY(-0.25rem)",
            },
          }}
        >
          {/* Large Blended Background Icon */}
          <Box
            sx={{
              position: "absolute",
              right: "-1.25rem",
              bottom: "-1.25rem",
              opacity: 0.15,
            }}
          >
            {accountStats.netFlow >= 0 ? (
              <TrendingUp sx={{ fontSize: "8.75rem", color: "#2E7D32" }} />
            ) : (
              <TrendingDown sx={{ fontSize: "8.75rem", color: "#C62828" }} />
            )}
          </Box>

          {/* Content */}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.03rem",
                fontSize: "0.7rem",
              }}
            >
              Net Flow
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: accountStats.netFlow >= 0 ? "#1B5E20" : "#C62828",
                mt: "0.5rem",
                fontSize: "1.5rem",
              }}
            >
              {accountStats.netFlow >= 0 ? "+" : "-"}$
              {Math.abs(accountStats.netFlow).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Data Table with Tabs */}
      <DataTable
        extraBtn={extraBtn}
        columns={columns}
        rows={rows}
        CustomToolbar={() => <CustomToolbarWithSearch />}
        CustomNoRowsOverlay={() => (
          <DataGridNoData
            imgSrc={moneyTransferImg}
            text={"No transactions available"}
          />
        )}
        loading={loading}
        page={page}
        setPage={setPage}
        pageSize={localPageSize}
        rowCount={rowCount}
        count={count}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        hasNextPage={nextPage !== null}
        hasPreviousPage={previousPage !== null}
        filterChips={activeFilterChips}
        height="calc(100vh - 11rem)"
      />

      {/* Delete Dialog */}
      <Dialog
        open={openDeleteDialog}
        TransitionComponent={Transition}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Delete Transaction Confirmation
          </Typography>
          {deleteTransaction && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Are you sure you want to delete this transaction? This action
              cannot be undone.
            </Typography>
          )}
        </DialogTitle>

        {deleteTransaction && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                backgroundColor: "grey.50",
                borderRadius: 2,
                p: 2,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Transaction Details:
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "120px" }}
                  >
                    Date:
                  </Typography>
                  <Typography variant="body2">
                    {deleteTransaction.date}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "120px" }}
                  >
                    Type:
                  </Typography>
                  <Chip
                    label={deleteTransaction.transaction_type}
                    size="small"
                    sx={{
                      backgroundColor:
                        filterOptions.transaction_type.find(
                          (t) => t.value === deleteTransaction.transaction_type,
                        )?.color + "15" || "#9E9E9E15",
                      color:
                        filterOptions.transaction_type.find(
                          (t) => t.value === deleteTransaction.transaction_type,
                        )?.color || "#9E9E9E",
                      border: `1px solid ${
                        filterOptions.transaction_type.find(
                          (t) => t.value === deleteTransaction.transaction_type,
                        )?.color || "#9E9E9E"
                      }30`,
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "120px" }}
                  >
                    Amount:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    ${deleteTransaction.amount || "0.00"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "120px" }}
                  >
                    Description:
                  </Typography>
                  <Typography variant="body2">
                    {deleteTransaction.description || "—"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "120px" }}
                  >
                    Status:
                  </Typography>
                  <Chip
                    label={deleteTransaction.status}
                    size="small"
                    sx={{
                      backgroundColor:
                        filterOptions.status.find(
                          (s) => s.value === deleteTransaction.status,
                        )?.color + "15" || "#9E9E9E15",
                      color:
                        filterOptions.status.find(
                          (s) => s.value === deleteTransaction.status,
                        )?.color || "#9E9E9E",
                      border: `1px solid ${
                        filterOptions.status.find(
                          (s) => s.value === deleteTransaction.status,
                        )?.color || "#9E9E9E"
                      }30`,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCloseDeleteDialog}
            sx={{ textTransform: "capitalize" }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={deleteLoad}
            size="small"
            variant="contained"
            color="error"
            onClick={handleDeleteTransaction}
            sx={{ textTransform: "capitalize" }}
          >
            Delete Transaction
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Filter Drawer */}
      <FilterDrawer
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        anchorEl={filterAnchor}
        isFilterApplied={isFilterApplied}
        title="Transaction Filters"
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
          {/* Transaction Type Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Filter by Transaction Type
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {filterOptions.transaction_type.map((type) => (
                <Chip
                  key={type.value}
                  label={type.label}
                  size="small"
                  clickable
                  color={
                    activeFilters.transaction_type.includes(type.value)
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleTransactionTypeFilter(type.value)}
                  variant={
                    activeFilters.transaction_type.includes(type.value)
                      ? "filled"
                      : "outlined"
                  }
                  sx={{
                    backgroundColor: activeFilters.transaction_type.includes(
                      type.value,
                    )
                      ? `${type.color}15`
                      : undefined,
                    color: activeFilters.transaction_type.includes(type.value)
                      ? type.color
                      : undefined,
                    borderColor: activeFilters.transaction_type.includes(
                      type.value,
                    )
                      ? type.color
                      : undefined,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Status Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Filter by Status
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {filterOptions.status.map((status) => (
                <Chip
                  key={status.value}
                  label={status.label}
                  size="small"
                  clickable
                  color={
                    activeFilters.status.includes(status.value)
                      ? "primary"
                      : "default"
                  }
                  onClick={() => handleStatusFilter(status.value)}
                  variant={
                    activeFilters.status.includes(status.value)
                      ? "filled"
                      : "outlined"
                  }
                  sx={{
                    backgroundColor: activeFilters.status.includes(status.value)
                      ? `${status.color}15`
                      : undefined,
                    color: activeFilters.status.includes(status.value)
                      ? status.color
                      : undefined,
                    borderColor: activeFilters.status.includes(status.value)
                      ? status.color
                      : undefined,
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Amount Range Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Filter by Amount
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                type="number"
                label="Min"
                value={activeFilters.amount_range.min || ""}
                onChange={(e) =>
                  handleAmountRangeChange(
                    e.target.value,
                    activeFilters.amount_range.max,
                  )
                }
                fullWidth
                InputProps={{ startAdornment: <Typography>$</Typography> }}
              />
              <TextField
                size="small"
                type="number"
                label="Max"
                value={activeFilters.amount_range.max || ""}
                onChange={(e) =>
                  handleAmountRangeChange(
                    activeFilters.amount_range.min,
                    e.target.value,
                  )
                }
                fullWidth
                InputProps={{ startAdornment: <Typography>$</Typography> }}
              />
            </Box>
          </Box>

          {/* Date Range Filters */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Date Range
            </Typography>
            <DateRangeFilter
              label="Transaction Date Range"
              onDateRangeChange={(range) =>
                handleDateRangeChange(range, "date")
              }
              dateType="date"
            />
          </Box>

          {/* Specific Date Filter */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Filter by Specific Date
            </Typography>
            <Select
              size="small"
              fullWidth
              value={selectedDateType}
              onChange={handleDateTypeChange}
              displayEmpty
            >
              <MenuItem value="">Select Date Type</MenuItem>
              {filterOptions.dateTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {selectedDateType && (
              <Box sx={{ mt: 1 }}>
                <DateCalendar
                  value={selectedSpecificDate}
                  onChange={handleSpecificDateChange}
                  sx={{ width: "100%" }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </FilterDrawer>
    </Box>
  );
};

export default BankTransaction;
